import { Phone, Activity, Clock, Wrench } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useStats } from '../../api/telemetry'
import { SpinLoader } from '../shared/SpinLoader'
import { ErrorCard } from '../shared/ErrorCard'
import { formatDuration } from '../../lib/utils'

// Voysi flamingo KPI grid. Visual reference: voysi-shell.jsx::KpiCard +
// screens-1.jsx (the four-up second row). 30px display value, 11px
// uppercase label, 28x28 flamingo-tinted icon badge top-right.

type StatKey = 'total_calls' | 'active_calls' | 'avg_duration_s' | 'total_tool_invocations'

const CARDS: { key: StatKey; labelKey: string; icon: LucideIcon }[] = [
  { key: 'total_calls', labelKey: 'dashboard.stats.totalCalls', icon: Phone },
  { key: 'active_calls', labelKey: 'dashboard.stats.activeCalls', icon: Activity },
  { key: 'avg_duration_s', labelKey: 'dashboard.stats.avgDuration', icon: Clock },
  { key: 'total_tool_invocations', labelKey: 'dashboard.stats.toolCalls', icon: Wrench },
]

interface KpiCardProps {
  label: string
  value: string
  icon: LucideIcon
  live?: boolean
}

function KpiCard({ label, value, icon: Icon, live }: KpiCardProps) {
  return (
    <div
      className="flex flex-col gap-1.5 rounded-2xl border border-ink-200 bg-white p-[18px]"
      style={{ boxShadow: 'var(--shadow-sm)' }}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-500">
          {label}
        </div>
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-flamingo-50 text-flamingo-500">
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <div className="mt-1 flex items-center gap-2 font-display text-[30px] font-extrabold leading-none tracking-tight text-ink-900">
        {value}
        {live && (
          <span
            className="h-2 w-2 rounded-full bg-success"
            style={{ animation: 'voysiPulse 1.4s infinite' }}
          />
        )}
      </div>
    </div>
  )
}

export function StatsBar() {
  const { t } = useTranslation('calls')
  const { data, isLoading, isError, refetch } = useStats()

  if (isLoading) return <SpinLoader size="sm" />
  if (isError) return <ErrorCard message={t('dashboard.loadStatsFailed')} onRetry={() => refetch()} />

  return (
    <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
      {CARDS.map(({ key, labelKey, icon }) => {
        const raw = data?.[key] ?? 0
        const value = key === 'avg_duration_s' ? formatDuration(raw) : raw.toLocaleString()
        const live = key === 'active_calls' && raw > 0
        return <KpiCard key={key} label={t(labelKey)} value={value} icon={icon} live={live} />
      })}
    </div>
  )
}
